// 📋 Sidebar Dinámico con Menús
import { marca } from '@brand';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, LucideIcon, MapPin } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useMenus, useAuth } from '@/lib/hooks';
import { apiClient } from '@/lib/api/client';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTabsStore } from '@/stores/tabs.store';
import type { Menu, Submenu } from '@/types/menu';

// Helper para obtener el componente de icono de Lucide
function getIconComponent(iconName: string | null): LucideIcon | null {
  if (!iconName) return null;

  // Buscar el icono en el objeto de iconos de Lucide
  const IconComponent = (LucideIcons as any)[iconName];

  return IconComponent || null;
}

interface SidebarProps {
  isCollapsed?: boolean;
}

export function Sidebar({ isCollapsed = false }: SidebarProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMenus();
  const { user } = useAuth();
  const { openTab, activeTabId } = useTabsStore();
  // Centros de costo asignados al usuario logoneado (se muestran en la cabecera)
  const { data: misCentros = [] } = useQuery<Array<{ id: string; razon_social: string }>>({
    queryKey: ['mis-centros-costo', user?.id],
    enabled: !!user?.id,
    queryFn: async () => (await apiClient.post('/rpc/get_usuario_centros_costo', { _usuario_id: user!.id })).data || [],
  });
  const centrosLabel = misCentros.map((c) => c.razon_social).join(', ');
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(new Set());

  // Rama (menú/submenú) que contiene la opción activa, para auto-expandir y resaltar
  const ramaActiva = useMemo(() => {
    for (const m of data?.menus ?? []) {
      for (const s of m.submenus ?? []) {
        for (const o of s.opciones ?? []) {
          if (o.ruta === activeTabId) return { menuId: m.id, submenuId: s.id };
        }
      }
    }
    return null;
  }, [data, activeTabId]);

  // Al cambiar la opción activa, asegurar que su menú y submenú estén expandidos
  // (sin colapsar lo que el usuario haya abierto manualmente).
  useEffect(() => {
    if (!ramaActiva) return;
    setExpandedMenus((prev) => (prev.has(ramaActiva.menuId) ? prev : new Set(prev).add(ramaActiva.menuId)));
    setExpandedSubmenus((prev) => (prev.has(ramaActiva.submenuId) ? prev : new Set(prev).add(ramaActiva.submenuId)));
  }, [ramaActiva]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  const toggleSubmenu = (submenuId: string) => {
    setExpandedSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(submenuId)) {
        newSet.delete(submenuId);
      } else {
        newSet.add(submenuId);
      }
      return newSet;
    });
  };

  const handleOptionClick = (opcion: { id: string; nombre: string; ruta: string; icon: string | null }) => {
    // Usar el sistema de tabs en lugar de navigate
    openTab({
      id: opcion.ruta, // Usar ruta como ID único
      title: opcion.nombre,
      path: opcion.ruta,
      icon: opcion.icon || undefined,
    });
  };

  if (isLoading) {
    return (
      <aside className={cn(
        'bg-card border-r border-border h-full flex items-center justify-center',
        isCollapsed ? 'w-16' : 'w-64'
      )}>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </aside>
    );
  }

  if (error || !data) {
    return (
      <aside className={cn(
        'bg-card border-r border-border h-full flex items-center justify-center p-4',
        isCollapsed ? 'w-16' : 'w-64'
      )}>
        <p className="text-sm text-destructive text-center">
          {isCollapsed ? '!' : t('shell.menus_error')}
        </p>
      </aside>
    );
  }

  return (
    <aside data-sidebar className={cn(
      'bg-card border-r border-border h-full flex flex-col transition-all duration-300',
      isCollapsed ? 'w-16' : 'w-64'
    )}>
      {/* Logotipo de la marca (solo marcas que lo definen; K2E no) */}
      {marca.Logotipo && (
        <div className="flex h-14 shrink-0 items-center border-b border-border px-4">
          <marca.Logotipo compacto={isCollapsed} />
        </div>
      )}
      {/* Header del sidebar: usuario · perfil (misma línea) + empresa */}
      <div className="p-4 border-b border-border">
        {!isCollapsed && (
          <div className="space-y-1">
            <h2 className="text-sm font-semibold text-foreground truncate">{data.empresa.nombre}</h2>
            {(user?.perfil?.nivel_acceso ?? data.perfil?.nivel_acceso ?? 0) >= 5 && (
              <div className="text-[10px] font-mono text-muted-foreground/70 truncate" title={`Servidor: ${window.location.origin}`}>
                🖥 {window.location.host}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="truncate">{user?.username || 'Usuario'}</span>
              <span>·</span>
              <span className="capitalize truncate">{data.perfil?.nombre || user?.perfil?.nombre || ''}</span>
            </div>
            {centrosLabel && (
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground/80" title={centrosLabel}>
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">{centrosLabel}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Menús */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        {data.menus.map((menu) => (
          <MenuItemComponent
            key={menu.id}
            menu={menu}
            isExpanded={expandedMenus.has(menu.id)}
            isCollapsed={isCollapsed}
            onToggle={() => toggleMenu(menu.id)}
            expandedSubmenus={expandedSubmenus}
            onToggleSubmenu={toggleSubmenu}
            onOptionClick={handleOptionClick}
            activeTabId={activeTabId}
            activeMenuId={ramaActiva?.menuId}
            activeSubmenuId={ramaActiva?.submenuId}
          />
        ))}
      </nav>
    </aside>
  );
}

interface MenuItemProps {
  menu: Menu;
  isExpanded: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  expandedSubmenus: Set<string>;
  onToggleSubmenu: (id: string) => void;
  onOptionClick: (opcion: { id: string; nombre: string; ruta: string; icon: string | null }) => void;
  activeTabId: string;
  activeMenuId?: string;
  activeSubmenuId?: string;
}

function MenuItemComponent({
  menu,
  isExpanded,
  isCollapsed,
  onToggle,
  expandedSubmenus,
  onToggleSubmenu,
  onOptionClick,
  activeTabId,
  activeMenuId,
  activeSubmenuId,
}: MenuItemProps) {
  const MenuIcon = getIconComponent(menu.icon);
  const esActivo = menu.id === activeMenuId;  // contiene la opción activa

  return (
    <div className="space-y-1">
      {/* Header del Menú */}
      <button
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 rounded-md',
          'text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          isExpanded ? 'bg-accent text-accent-foreground' : esActivo ? 'text-accent font-semibold' : 'text-muted-foreground'
        )}
        title={isCollapsed ? menu.nombre : undefined}
      >
        {MenuIcon ? (
          <MenuIcon className="w-5 h-5" />
        ) : (
          <span className="text-lg">📋</span>
        )}
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left">{menu.nombre}</span>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </>
        )}
      </button>

      {/* Submenús */}
      <AnimatePresence>
        {isExpanded && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-4 space-y-1 overflow-hidden"
          >
            {menu.submenus.map((submenu) => (
              <SubmenuItemComponent
                key={submenu.id}
                submenu={submenu}
                isExpanded={expandedSubmenus.has(submenu.id)}
                onToggle={() => onToggleSubmenu(submenu.id)}
                onOptionClick={onOptionClick}
                activeTabId={activeTabId}
                esActiva={submenu.id === activeSubmenuId}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface SubmenuItemProps {
  submenu: Submenu;
  isExpanded: boolean;
  onToggle: () => void;
  onOptionClick: (opcion: { id: string; nombre: string; ruta: string; icon: string | null }) => void;
  activeTabId: string;
  esActiva?: boolean;
}

function SubmenuItemComponent({
  submenu,
  isExpanded,
  onToggle,
  onOptionClick,
  activeTabId,
  esActiva = false,
}: SubmenuItemProps) {
  return (
    <div className="space-y-1">
      {/* Header del Submenú */}
      <button
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-md',
          'text-xs font-medium transition-colors',
          // El fondo del sidebar es `card`; usar text-accent-foreground sin bg-accent
          // dejaba el texto sin contraste (blanco/negro invisible). Se usa text-foreground
          // (legible en ambos temas) y el hover empareja fondo+texto accent.
          'hover:bg-accent hover:text-accent-foreground',
          // resalta la rama que contiene la opción activa
          esActiva ? 'text-accent font-semibold' : isExpanded ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        <span className="flex-1 text-left">{submenu.nombre}</span>
        {isExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
      </button>

      {/* Opciones */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="ml-2 space-y-0.5 overflow-hidden"
          >
            {submenu.opciones.map((opcion) => {
              const isActive = activeTabId === opcion.ruta;
              const OptionIcon = getIconComponent(opcion.icon);

              return (
                <button
                  key={opcion.id}
                  onClick={() => onOptionClick({
                    id: opcion.id,
                    nombre: opcion.nombre,
                    ruta: opcion.ruta,
                    icon: opcion.icon
                  })}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 rounded-md',
                    'text-xs transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:bg-accent/30 hover:text-foreground'
                  )}
                  title={opcion.descripcion || undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {OptionIcon && <OptionIcon className="w-4 h-4" />}
                  <span className="flex-1 text-left">{opcion.nombre}</span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
